import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import router from '@/router';
import { LINKS } from '@/utils/links';
import { useSponsorshipCode } from '@/composables/useSponsorshipCode';
import { isLoggedIn } from '@/services/auth';
import { LIBHEROS_DEEPLINK_URL } from '@/config/api.config';

// https://capacitorjs.com/docs/guides/deep-links
/**
 * Custom URL scheme used for deep links.
 * e.g. libheros://appointments/123
 *      libheros://tabs/dashboard
 */
const SCHEME = LIBHEROS_DEEPLINK_URL;

interface DeepLinkTarget {
  path: string;
  query: Record<string, string>;
}

/**
 * Route ouverte via deep link mais dont l'utilisateur n'était pas encore
 * connecté au moment de la réception. Consommée après le login.
 */
let pendingRoute: DeepLinkTarget | null = null;
let deepLinkListenerActive = false;

/**
 * Parse une URL entrante et extrait le path interne + les query params.
 *
 * Formats supportés :
 *   libheros://appointments/123          → /appointments/123
 *   libheros://tabs/dashboard            → /tabs/dashboard
 *   libheros://auth/sign-up?code=ABC     → query.code = 'ABC'
 *   https://app.libheros.fr/#/notification/5  → /notification/5
 *   https://app.libheros.fr/appointments/123  → /appointments/123
 */
const parseDeepLink = (url: string): DeepLinkTarget | null => {
  try {
    const parsed = new URL(url);
    const query: Record<string, string> = {};
    parsed.searchParams.forEach((value, key) => {
      query[key] = value;
    });

    let path = '';

    if (parsed.protocol === `${SCHEME}:`) {
      // libheros://appointments/123  →  host="appointments", pathname="/123"
      // libheros://tabs/dashboard    →  host="tabs",         pathname="/dashboard"
      path = `/${parsed.hostname}${parsed.pathname}`.replace(/\/+$/, '') || LINKS.TABS.DASHBOARD;
    } else if (parsed.protocol === 'https:' || parsed.protocol === 'http:') {
      if (parsed.hash.startsWith('#/')) {
        // Hash routing : https://…/#/appointments/123?foo=bar
        const withoutHash = parsed.hash.slice(1);
        const [hashPath, hashSearch] = withoutHash.split('?');
        path = hashPath;
        if (hashSearch) {
          new URLSearchParams(hashSearch).forEach((value, key) => {
            query[key] = value;
          });
        }
      } else {
        path = parsed.pathname;
      }
    }

    if (!path || path === '/') return null;
    return { path, query };
  } catch {
    return null;
  }
};

export function useDeepLinks() {
  const { setCode } = useSponsorshipCode();

  /**
   * Traite une URL de deep link et navigue vers la route correspondante.
   * Si l'utilisateur n'est pas connecté, la route est mise en attente
   * pour être consommée après le login (via consumePendingRoute).
   */
  const handleDeepLink = async (url: string): Promise<void> => {
    const target = parseDeepLink(url);
    if (!target) return;

    const { path, query } = target;

    // Code de parrainage — toujours traité en priorité, même sans session
    if (query.code) {
      setCode(query.code);
      await router.push(LINKS.AUTH.SIGN_UP.PRO_STEP);
      return;
    }

    const resolved = router.resolve({ path });
    if (!resolved.matched.length) return;

    const logged = await isLoggedIn();
    if (!logged) {
      // Mémorise la destination, le router guard redirigera vers le login
      pendingRoute = { path, query };
      return;
    }

    try {
      await router.push({ path, query });
    } catch {
      // NavigationDuplicated — on ignore
    }
  };

  /**
   * Appelé après un login réussi.
   * Si un deep link avait été reçu avant l'authentification, navigue vers
   * cette destination au lieu du dashboard par défaut.
   * @returns true si une route en attente a été consommée
   */
  const consumePendingRoute = async (): Promise<boolean> => {
    if (!pendingRoute) return false;
    const { path, query } = pendingRoute;
    pendingRoute = null;
    try {
      await router.push({ path, query });
    } catch {
      // Route invalide — on laisse le comportement par défaut
      return false;
    }
    return true;
  };

  /**
   * À appeler une seule fois au démarrage de l'app (App.vue onMounted).
   * Met en place les listeners natifs pour les deep links.
   */
  const initDeepLinks = async (): Promise<void> => {
    if (!Capacitor.isNativePlatform()) return;

    // Cold start : app ouverte via deep link alors qu'elle ne tournait pas
    const launchUrl = await App.getLaunchUrl();
    if (launchUrl?.url) {
      await handleDeepLink(launchUrl.url);
    }

    // Guard : enregistre le listener une seule fois même si initDeepLinks est rappelé (HMR)
    if (deepLinkListenerActive) return;
    deepLinkListenerActive = true;

    // App déjà en mémoire : arrive via appUrlOpen
    App.addListener('appUrlOpen', ({ url }) => {
      void handleDeepLink(url);
    });
  };

  return { initDeepLinks, handleDeepLink, consumePendingRoute };
}
